import useAuth from 'hooks/useAuth';
import { useMutation, useQuery } from 'react-query';
import { FormBuilder } from 'types/formBuilder';

interface Props {
  userId?: string;
}
interface UpdateFormProps {
  payload: any;
  status?: boolean;
  archive?: boolean;
  formId: string;
}

export default function useFormBuilder({ userId }: Props) {
  const { request } = useAuth();

  const {
    data: forms,
    refetch: refetchForms,
    isLoading
  } = useQuery(
    '/form-builder/forms',
    () => request.get('/form-builder/forms').then(async (response) => (await response.data.forms) as FormBuilder[]),
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      enabled: !!userId
    }
  );

  const {
    data: userForms,
    refetch,
    isLoading: userFormsIsLoading
  } = useQuery(
    `/form-builder/forms/${userId}`,
    () =>
      request.get(`/form-builder/forms/${userId}`).then((response) => {
        return response.data;
      }),
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      enabled: !!userId
    }
  );

  const { mutateAsync: createForm, isLoading: createdFormLoading } = useMutation(
    '/form-builder/forms',
    (payload: any) => request.post(`/form-builder/forms/${userId}`, payload),
    { onSuccess: () => refetch() }
  );

  const { mutateAsync: updateForm, isLoading: updatedFormLoading } = useMutation(
    '/form-builder/forms',
    ({ payload, status, archive, formId }: UpdateFormProps) =>
      request.put(`/form-builder/forms/${userId}/${formId}?status=${status ?? ''}&archive=${archive ?? ''}`, payload),
    { onSuccess: () => refetch() }
  );

  const { mutateAsync: deleteForm } = useMutation(
    '/form-builder/forms',
    (formId) => request.delete(`/form-builder/forms/${userId}/${formId}`).then(({ data }) => data as any),
    { onSuccess: () => refetch() }
  );

  return {
    userForms,
    refetchForms,
    userFormsIsLoading,
    forms,
    refetch,
    isLoading,
    updateForm,
    updatedFormLoading,
    deleteForm,
    createForm,
    createdFormLoading
  };
}
